import { Directive, Input, Optional, TemplateRef, ViewContainerRef } from "@angular/core";
import { ControlContainer } from "@angular/forms";

/**
 * Directive to easily access a AbstractControl from a parent FormGroup in the template by the formControlName.
 * It uses the current context of the parent FormGroup and looks for a control with the given name.
 *                  
 * We often have the issue of accessing information of controls which depend on 
 * deeply nested controls inside form groups.
 * 
 * For example, we want to explain the error of the FormControl based on the attached error property. 
 * With this directive, we can easily access the FormControl in the template and use its properties
 * without having to do awkward accessor logic in the HAML by accessing groups of groups in arrays of groups.
 * 
 * @example      
 * TypeScript:
 * ```ts
 * const form = new FormGroup({
 *     address: new FormControl(''),
 * });
 * ``` 
 * HTML:
 * ```html
 * <!-- The control will be available as 'control' in the template context.-->
 * <ng-container *vControl="'address' as control"> {{control.valid}} </ng-container>
 * ```
 */
@Directive({
    selector:   '[vControl]',
    standalone: true,
})
export class VControlDirective {
    @Input() set vControl(context: unknown) {
        if (!this.controlContainer) return;
        if (typeof context !== "string") return;

        const control = this.controlContainer.control.get(context);
        if (!control) console.warn(`FormControl with name '${context}' not found in parent FormGroup.`);

        this.context.$implicit = this.context.vControl = control;

        if (!this.hasView) {
            this.vcRef.createEmbeddedView(this.templateRef, this.context);
            this.hasView = true;
        }
    }
    private context: {
        $implicit: unknown;
        vControl: unknown;
    } = {
        $implicit: null,
        vControl:  null,
    };

    private hasView: boolean = false;

    constructor(
        @Optional() protected controlContainer: ControlContainer,
        private templateRef: TemplateRef<unknown>,
        private vcRef: ViewContainerRef
    ) {}
}
